// components/website/Header.js - Removed search and cart icons in mobile view
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { useProducts } from '../../hooks/useProducts';
import { dbOnValue, dbGet } from '../../config/firebase';
import {
  ShoppingCart, User, Menu, X, LogOut, Package, 
  Search, TrendingUp, Clock, Flower2, Home, Heart, Gift,
  Crown, Flower, Palette, ChevronRight, Sparkles, Grid, Phone, Mail, MapPin,
  Leaf, Cake, Gift as GiftIcon, ArrowLeft
} from 'lucide-react';
import { toast } from 'react-toastify';

// Custom centered toast for Header
const showCenteredToast = (message, type = 'success') => {
  toast[type](message, {
    position: "top-center",
    autoClose: 2000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    theme: "light",
    style: {
      borderRadius: '12px',
      background: type === 'success' ? '#dc2626' : '#ef4444',
      color: 'white',
      fontSize: '14px',
      fontWeight: 500,
      textAlign: 'center'
    }
  });
};

// Mobile Search Modal - Full screen with metadata search
const MobileSearchModal = ({ isOpen, onClose, onSearch, allProducts, recentSearches, onClearRecent, onAddToRecent }) => {
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const inputRef = useRef(null);

  const popularSearches = [
    { icon: TrendingUp, label: 'Birthday Flowers', query: 'birthday' },
    { icon: Heart, label: 'Red Roses', query: 'roses' },
    { icon: Crown, label: 'Luxury Bouquets', query: 'luxury' },
    { icon: Gift, label: 'Anniversary', query: 'anniversary' }
  ];

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchInput.trim() && allProducts && allProducts.length > 0) {
      const query = searchInput.toLowerCase();
      const filtered = allProducts.filter(p =>
        p.name?.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query) ||
        p.tag?.toLowerCase().includes(query)
      ).slice(0, 5);
      setSuggestions(filtered);
    } else {
      setSuggestions([]);
    }
  }, [searchInput, allProducts]);

  const handleSearch = () => {
    if (searchInput.trim()) {
      onAddToRecent(searchInput.trim());
      onSearch(searchInput.trim());
      onClose();
      setSearchInput('');
    }
  };

  const handleSuggestionClick = (product) => {
    onAddToRecent(product.name);
    onSearch(product.name);
    onClose();
    setSearchInput('');
    window.location.href = `/product/${product.id}`;
  };

  if (!isOpen) return null;

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000,
        backdropFilter: 'blur(4px)', animation: 'fadeIn 0.2s ease'
      }} />
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'white', zIndex: 2001, overflowY: 'auto',
        animation: 'slideUp 0.3s ease'
      }}>
        <div style={{ padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <button onClick={onClose} style={{
              width: '40px', height: '40px', borderRadius: '8px',
              background: '#f1f5f9', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <ArrowLeft size={20} color="#dc2626" />
            </button>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 600, margin: 0, color: '#1a0a0a' }}>Search Products</h2>
          </div>

          <div style={{ position: 'relative', marginBottom: '24px' }}>
            <Search size={18} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#dc2626' }} />
            <input
              ref={inputRef}
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search for flowers, bouquets..."
              style={{
                width: '100%',
                padding: '14px 16px 14px 44px',
                border: '2px solid #ffe0e0',
                borderRadius: '30px',
                fontSize: '16px',
                outline: 'none',
                background: '#fff5f5'
              }}
            />
          </div>

          {/* Recent Searches Section */}
          {recentSearches && recentSearches.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Clock size={14} color="#dc2626" />
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#dc2626' }}>Recent Searches</span>
                </div>
                <button onClick={onClearRecent} style={{ fontSize: '11px', color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>Clear All</button>
              </div>
              {recentSearches.slice(0, 5).map((s, idx) => (
                <button
                  key={idx}
                  onClick={() => { setSearchInput(s); setTimeout(handleSearch, 100); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    width: '100%', padding: '10px 12px', background: '#fff5f5',
                    border: 'none', borderRadius: '10px', marginBottom: '6px',
                    cursor: 'pointer'
                  }}
                >
                  <Clock size={14} color="#dc2626" />
                  <span style={{ fontSize: '13px', color: '#1a0a0a' }}>{s}</span>
                </button>
              ))}
            </div>
          )}

          {/* Popular Searches Section */}
          <div style={{ marginBottom: suggestions.length > 0 ? '24px' : 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
              <TrendingUp size={14} color="#dc2626" />
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#dc2626' }}>Popular Searches</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {popularSearches.map((item, idx) => (
                <button
                  key={idx}
                  onClick={() => { setSearchInput(item.query); setTimeout(handleSearch, 100); }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '8px 14px', background: '#fff5f5', border: '1px solid #ffe0e0',
                    borderRadius: '30px', cursor: 'pointer', fontSize: '12px',
                    color: '#1a0a0a'
                  }}
                >
                  <item.icon size={14} color="#dc2626" />
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Product Suggestions */}
          {suggestions.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#dc2626', marginBottom: '12px' }}>Product Suggestions</div>
              {suggestions.map((p, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSuggestionClick(p)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    width: '100%', padding: '10px 12px', background: '#f8fafc',
                    border: 'none', borderRadius: '10px', marginBottom: '8px',
                    cursor: 'pointer'
                  }}
                >
                  <img src={p.image} alt={p.name} style={{ width: '48px', height: '48px', borderRadius: '8px', objectFit: 'cover' }} />
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <div style={{ fontSize: '13px', fontWeight: 500, color: '#1a0a0a' }}>{p.name}</div>
                    <div style={{ fontSize: '12px', color: '#dc2626', fontWeight: 600 }}>₹{p.price?.toLocaleString()}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
      `}</style>
    </>
  );
};

const Header = ({ onCartClick, onProfileClick, onSearch, searchQuery: externalSearchQuery }) => {
  const { user, isAdmin, logout } = useAuth();
  const { totalItems } = useCart();
  const { products } = useProducts();
  const [mobileMenu, setMobileMenu] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Recent searches state
  const [recentSearches, setRecentSearches] = useState(() => {
    try { 
      const saved = localStorage.getItem('recent_searches');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  // Load categories from Firebase with real-time listener
  useEffect(() => {
    const unsubscribe = dbOnValue('shop_categories', (snapshot) => {
      const data = snapshot.val();
      console.log('Categories from Firebase:', data);
      
      if (data) {
        // If data is an object, convert to array
        let categoriesArray = [];
        if (Array.isArray(data)) {
          categoriesArray = data;
        } else if (typeof data === 'object') {
          categoriesArray = Object.entries(data).map(([key, value]) => ({
            id: key,
            ...value
          }));
        }
        
        // Filter out any null/undefined and ensure each category has required fields
        const validCategories = categoriesArray.filter(cat => cat && typeof cat === 'object').map(cat => ({
          id: cat.id || `cat_${Math.random()}`,
          name: cat.name || 'Category',
          icon: cat.icon || '📦'
        }));
        
        console.log('Valid categories:', validCategories);
        setCategories(validCategories);
      } else {
        // If no data in Firebase, set empty array (don't create defaults)
        setCategories([]);
      }
      setLoadingCategories(false);
    }, (error) => {
      console.error('Error fetching categories:', error);
      setCategories([]);
      setLoadingCategories(false);
    });
    
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const allProducts = useMemo(() => {
    if (!products) return [];
    return [
      ...(products.occasion || []),
      ...(products.luxury || []),
      ...(products.exotic || []),
      ...(products.decor || [])
    ];
  }, [products]);

  // Search suggestions for desktop
  const suggestions = useMemo(() => {
    if (!searchInput.trim() || !allProducts.length) return [];
    const query = searchInput.toLowerCase();
    return allProducts
      .filter(p =>
        p.name?.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query) ||
        p.tag?.toLowerCase().includes(query)
      )
      .slice(0, 5);
  }, [searchInput, allProducts]);

  const popularSearches = [
    { icon: TrendingUp, label: 'Birthday Flowers', query: 'birthday' },
    { icon: Heart, label: 'Red Roses', query: 'roses' },
    { icon: Crown, label: 'Luxury Bouquets', query: 'luxury' },
    { icon: Gift, label: 'Anniversary', query: 'anniversary' }
  ];

  const addToRecentSearches = (query) => {
    const updated = [query, ...recentSearches.filter(s => s !== query)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('recent_searches', JSON.stringify(updated));
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recent_searches');
    showCenteredToast('Recent searches cleared', 'success');
  };

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
    setShowSuggestions(true);
  };

  const handleSearchSubmit = (query = searchInput) => {
    if (query.trim()) {
      addToRecentSearches(query.trim());
      if (onSearch) onSearch(query.trim());
      setShowSuggestions(false);
      setSearchInput('');
    }
  };

  const handleSuggestionClick = (product) => {
    addToRecentSearches(product.name);
    navigate(`/product/${product.id}`);
    setShowSuggestions(false);
    setSearchInput('');
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target))
        setShowSuggestions(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenu) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [mobileMenu]);

  const navigateToCategory = (category) => {
    navigate(`/products/${category}`);
    setMobileMenu(false);
  };

  const handleShopByCategory = (category) => {
    if (category && category.id) {
      navigate(`/category/${category.id}`, { state: { category } });
    }
    setMobileMenu(false);
  };

  const handleAllCategories = () => {
    navigate('/all-categories');
    setMobileMenu(false);
  };

  const handleDecorationEnquiry = () => {
    navigate('/decoration-enquiry');
    setMobileMenu(false);
  };

  const handleDecorClick = () => {
    if (window.location.pathname === '/') {
      const decorSection = document.getElementById('decor');
      if (decorSection) {
        decorSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      navigate('/');
      setTimeout(() => {
        const decorSection = document.getElementById('decor');
        if (decorSection) decorSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
    }
    setMobileMenu(false);
  };

  const userInitial = user?.name ? user.name.charAt(0).toUpperCase() : '?';

  const mobileNavStyle = {
    display: 'flex', 
    alignItems: 'center', 
    gap: '14px', 
    width: '100%',
    padding: '12px 20px', 
    background: 'none', 
    border: 'none',
    color: '#1a0a0a', 
    fontSize: '14px', 
    fontWeight: 500,
    cursor: 'pointer', 
    textAlign: 'left',
    transition: 'all 0.2s'
  };

  return (
    <>
      {/* Main Header - Cherry Theme */}
      <header style={{
        background: '#dc2626',
        color: '#fff',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        width: '100%'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: isMobile ? '56px' : '60px',
          padding: '0 16px',
          gap: '12px',
          maxWidth: '1280px',
          margin: '0 auto',
          width: '100%'
        }}>
          {/* Hamburger Button - Mobile only */}
          <button 
            onClick={() => setMobileMenu(true)}
            style={{
              background: 'none',
              border: 'none',
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              display: isMobile ? 'flex' : 'none',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'white'
            }}
          >
            <Menu size={20} />
          </button>

          {/* Logo */}
          {/* Logo */}
<Link to="/" style={{
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  color: 'white',
  textDecoration: 'none',
  flexShrink: 0
}}>
  <div style={{
    width: isMobile ? '40px' : '45px',
    height: isMobile ? '40px' : '45px',
    background: 'white',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    overflow: 'hidden',
    padding: '6px'
  }}>
    <img 
      src="unnamed.jpg" 
      alt="Sugandhi Flowers" 
      style={{ 
        width: '100%', 
        height: '100%', 
        objectFit: 'contain',
        display: 'block'
      }}
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Crect width="100" height="100" fill="%23dc2626"/%3E%3Ctext x="50" y="67" text-anchor="middle" fill="white" font-size="50" font-weight="bold"%3ES%3C/text%3E%3C/svg%3E';
      }}
    />
  </div>
  <div style={{ lineHeight: 1.1 }}>
    <div style={{ fontWeight: 700, fontSize: isMobile ? '1rem' : '1.1rem' }}>Sugandhi</div>
    <div style={{ fontSize: '0.6rem', fontWeight: '500', opacity: 0.9 }}>Flowers</div>
  </div>
</Link>
          {/* Search Bar - Desktop */}
          {!isMobile && (
            <div ref={searchRef} style={{
              flex: 1,
              maxWidth: '500px',
              margin: '0 16px',
              position: 'relative'
            }}>
              <div style={{ position: 'relative', width: '100%' }}>
                <input
                  type="text"
                  placeholder="Search for flowers, bouquets..."
                  value={searchInput}
                  onChange={handleSearchChange}
                  onFocus={() => setShowSuggestions(true)}
                  style={{
                    width: '100%',
                    padding: '10px 48px 10px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    fontSize: '14px',
                    background: 'white',
                    color: '#212121',
                    outline: 'none'
                  }}
                />
                <button
                  onClick={() => handleSearchSubmit()}
                  style={{
                    position: 'absolute',
                    right: '4px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    background: '#ff9f00',
                    border: 'none',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <Search size={16} />
                </button>
              </div>
              
              {/* Search Suggestions Dropdown - Desktop */}
              {showSuggestions && (
                <div style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  left: 0,
                  right: 0,
                  background: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  zIndex: 1001,
                  maxHeight: 400,
                  overflow: 'auto',
                  border: '1px solid #f0f0f0'
                }}>
                  <div style={{ padding: '12px' }}>
                    {/* Recent Searches Section - Desktop */}
                    {recentSearches.length > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          marginBottom: '8px',
                          padding: '0 8px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Clock size={14} color="#dc2626" />
                            <span style={{ fontSize: '11px', fontWeight: 600, color: '#dc2626' }}>Recent Searches</span>
                          </div>
                          <button 
                            onClick={clearRecentSearches}
                            style={{ fontSize: '10px', color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}
                          >
                            Clear All
                          </button>
                        </div>
                        {recentSearches.slice(0, 5).map((s, idx) => (
                          <button 
                            key={idx} 
                            onClick={() => handleSearchSubmit(s)} 
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              width: '100%',
                              padding: '8px 12px',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              borderRadius: '8px',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#fff5f5'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                          >
                            <Clock size={14} color="#dc2626" />
                            <span style={{ fontSize: '13px', color: '#212121' }}>{s}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {/* Popular Searches Section - Desktop */}
                    <div style={{ marginBottom: suggestions.length > 0 ? '16px' : 0 }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '6px',
                        marginBottom: '8px',
                        padding: '0 8px'
                      }}>
                        <TrendingUp size={14} color="#dc2626" />
                        <span style={{ fontSize: '11px', fontWeight: 600, color: '#dc2626' }}>Popular Searches</span>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {popularSearches.map((item, idx) => (
                          <button 
                            key={idx} 
                            onClick={() => handleSearchSubmit(item.query)} 
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '6px 12px',
                              background: '#fff5f5',
                              border: '1px solid #ffe0e0',
                              borderRadius: '30px',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              fontSize: '12px'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#dc2626';
                              e.currentTarget.style.color = 'white';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#fff5f5';
                              e.currentTarget.style.color = '#212121';
                            }}
                          >
                            <item.icon size={14} />
                            <span>{item.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Product Suggestions Section - Desktop */}
                    {suggestions.length > 0 && (
                      <div style={{ marginTop: '16px' }}>
                        <div style={{ 
                          padding: '0 8px',
                          marginBottom: '8px',
                          fontSize: '11px', 
                          fontWeight: 600, 
                          color: '#dc2626' 
                        }}>
                          Products
                        </div>
                        {suggestions.map((p, idx) => (
                          <button 
                            key={idx} 
                            onClick={() => handleSuggestionClick(p)} 
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              width: '100%',
                              padding: '8px 12px',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              borderRadius: '8px',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#fff5f5'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                          >
                            <img 
                              src={p.image} 
                              alt={p.name} 
                              style={{ width: '36px', height: '36px', borderRadius: '8px', objectFit: 'cover' }}
                              onError={(e) => e.target.src = 'https://images.unsplash.com/photo-1591886960571-74d43a9d4166?w=40&q=80'}
                            />
                            <div style={{ flex: 1, textAlign: 'left' }}>
                              <div style={{ fontSize: '13px', fontWeight: 500, color: '#212121' }}>{p.name}</div>
                              <div style={{ fontSize: '11px', color: '#dc2626', fontWeight: 600 }}>₹{p.price?.toLocaleString()}</div>
                            </div>
                            <Search size={14} color="#dc2626" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Desktop Navigation */}
          {!isMobile && (
            <nav style={{ display: 'flex', gap: '8px' }}>
              <button onClick={handleAllCategories} style={{
                background: 'none', border: 'none', color: 'white', fontWeight: 500,
                fontSize: '14px', cursor: 'pointer', padding: '8px 12px', borderRadius: '8px',
                display: 'flex', alignItems: 'center', gap: '6px'
              }}>
                <Grid size={15} /> Shop by Category
              </button>
              <button onClick={() => navigate('/products/occasions')} style={{
                background: 'none', border: 'none', color: 'white', fontWeight: 500,
                fontSize: '14px', cursor: 'pointer', padding: '8px 12px', borderRadius: '8px'
              }}>Occasions</button>
              <button onClick={() => navigate('/products/luxury')} style={{
                background: 'none', border: 'none', color: 'white', fontWeight: 500,
                fontSize: '14px', cursor: 'pointer', padding: '8px 12px', borderRadius: '8px'
              }}>Luxury</button>
              <button onClick={handleDecorClick} style={{
                background: 'none', border: 'none', color: 'white', fontWeight: 500,
                fontSize: '14px', cursor: 'pointer', padding: '8px 12px', borderRadius: '8px'
              }}>Decor</button>
              <button onClick={handleDecorationEnquiry} style={{
                background: 'none', border: 'none', color: 'white', fontWeight: 500,
                fontSize: '14px', cursor: 'pointer', padding: '8px 12px', borderRadius: '8px',
                display: 'flex', alignItems: 'center', gap: '6px'
              }}><Palette size={15} /> Enquiry</button>
            </nav>
          )}

          {/* Header Actions */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {/* Mobile Search Button - REMOVED */}
            {/* Cart Icon - REMOVED for mobile */}

            {/* Cart Button - Desktop only */}
            {!isMobile && (
              <button onClick={onCartClick} style={{
                position: 'relative',
                background: 'rgba(255,255,255,0.15)',
                border: 'none',
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}>
                <ShoppingCart size={18} />
                {totalItems > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                    background: '#ff9f00',
                    color: '#fff',
                    fontSize: '10px',
                    fontWeight: 700,
                    minWidth: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>{totalItems}</span>
                )}
              </button>
            )}

            {user ? (
              <div style={{ position: 'relative' }}>
                <button onClick={() => setUserMenu(!userMenu)} style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#dc2626',
                  fontWeight: 700,
                  fontSize: '1rem',
                  border: 'none',
                  cursor: 'pointer'
                }}>{userInitial}</button>
                {userMenu && (
                  <div style={{
                    position: 'absolute',
                    right: 0,
                    top: 'calc(100% + 8px)',
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    minWidth: 200,
                    zIndex: 1000,
                    overflow: 'hidden'
                  }}>
                    <div style={{ padding: '10px 16px', background: '#fff5f5', borderBottom: '1px solid #ffe0e0' }}>
                      <div style={{ fontWeight: 600, color: '#dc2626' }}>{user.name || 'User'}</div>
                      <div style={{ fontSize: '11px', color: '#878787' }}>{user.email}</div>
                    </div>
                    <button onClick={() => { onProfileClick(); setUserMenu(false); }} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
                      width: '100%', background: 'none', border: 'none', fontSize: '14px',
                      color: '#212121', cursor: 'pointer', textAlign: 'left'
                    }}><User size={14} /> My Profile</button>
                    <button onClick={() => { onProfileClick(); setUserMenu(false); }} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
                      width: '100%', background: 'none', border: 'none', fontSize: '14px',
                      color: '#212121', cursor: 'pointer', textAlign: 'left'
                    }}><Package size={14} /> My Orders</button>
                    <hr style={{ margin: 0 }} />
                    <button onClick={() => { logout(); setUserMenu(false); }} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
                      width: '100%', background: 'none', border: 'none', fontSize: '14px',
                      color: '#dc2626', cursor: 'pointer', textAlign: 'left'
                    }}><LogOut size={14} /> Logout</button>
                  </div>
                )}
              </div>
            ) : (
              <button onClick={() => navigate('/login')} style={{
                padding: '8px 20px',
                background: 'white',
                color: '#dc2626',
                fontWeight: 600,
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px'
              }}>Login</button>
            )}
          </div>
        </div>

        {/* Mobile Search Bar - Simple input that opens modal */}
        {isMobile && (
          <div style={{ padding: '8px 16px 12px', background: '#dc2626' }}>
            <div 
              onClick={() => setShowMobileSearch(true)}
              style={{
                position: 'relative',
                width: '100%',
                background: 'white',
                borderRadius: '8px',
                padding: '10px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer'
              }}
            >
              <Search size={16} color="#dc2626" />
              <span style={{ color: '#999', fontSize: '14px' }}>Search for flowers, bouquets...</span>
            </div>
          </div>
        )}
      </header>

      {/* Mobile Side Drawer */}
      {mobileMenu && (
        <>
          <div onClick={() => setMobileMenu(false)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1998, backdropFilter: 'blur(2px)'
          }} />
          <div style={{
            position: 'fixed', top: 0, left: 0, bottom: 0, width: '280px',
            background: 'white', zIndex: 1999, overflowY: 'auto',
            boxShadow: '2px 0 12px rgba(0,0,0,0.15)',
            animation: 'slideInLeft 0.3s ease'
          }}>
            <div style={{
              padding: '20px', background: '#dc2626', color: 'white',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between'
            }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.2rem' }}>Sugandhi Flowers</div>
                <div style={{ fontSize: '11px', opacity: 0.9 }}>Premium Florist</div>
              </div>
              <button onClick={() => setMobileMenu(false)} style={{
                background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
                width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer'
              }}><X size={18} /></button>
            </div>
            
            <nav style={{ padding: '12px 0' }}>
              <div style={{ padding: '8px 16px', fontSize: '11px', fontWeight: 600, color: '#dc2626', marginTop: '8px' }}>SHOP BY CATEGORY</div>
              
              {/* Safe mapping with null check */}
              {categories && categories.length > 0 ? (
                categories.slice(0, 8).map((category) => {
                  // Safety check for category object
                  if (!category) return null;
                  return (
                    <button 
                      key={category.id || Math.random()} 
                      onClick={() => handleShopByCategory(category)} 
                      style={mobileNavStyle}
                    >
                      <span style={{ fontSize: '18px', marginRight: '4px' }}>{category.icon || '📦'}</span>
                      <span style={{ flex: 1, textAlign: 'left' }}>{category.name || 'Category'}</span>
                      <ChevronRight size={14} color="#dc2626" />
                    </button>
                  );
                })
              ) : (
                <div style={{ padding: '12px 20px', color: '#64748b', fontSize: '12px', textAlign: 'center' }}>
                  No categories available
                </div>
              )}
              
              <div style={{ height: '1px', background: '#f0f0f0', margin: '12px 16px' }} />
              
              <div style={{ padding: '8px 16px', fontSize: '11px', fontWeight: 600, color: '#dc2626' }}>SERVICES</div>
              <button onClick={handleDecorationEnquiry} style={mobileNavStyle}>
                <Palette size={18} color="#dc2626" /> <span style={{ flex: 1 }}>Decoration Enquiry</span>
              </button>
              
              <div style={{ height: '1px', background: '#f0f0f0', margin: '12px 16px' }} />
              
              <div style={{ padding: '8px 16px', fontSize: '11px', fontWeight: 600, color: '#dc2626' }}>QUICK LINKS</div>
              <button onClick={() => { navigate('/'); setMobileMenu(false); }} style={mobileNavStyle}>
                <Home size={18} color="#dc2626" /> <span style={{ flex: 1 }}>Home</span>
              </button>
              <button onClick={() => navigateToCategory('bestsellers')} style={mobileNavStyle}>
                <Heart size={18} color="#dc2626" /> <span style={{ flex: 1 }}>Best Sellers</span>
              </button>
              <button onClick={() => navigateToCategory('recommended')} style={mobileNavStyle}>
                <Sparkles size={18} color="#dc2626" /> <span style={{ flex: 1 }}>Recommended</span>
              </button>
              
              <div style={{ height: '1px', background: '#f0f0f0', margin: '12px 16px' }} />
              
              <div style={{ padding: '8px 16px', fontSize: '11px', fontWeight: 600, color: '#dc2626' }}>ACCOUNT</div>
              {user ? (
                <>
                  <button onClick={() => { onProfileClick(); setMobileMenu(false); }} style={mobileNavStyle}>
                    <User size={18} color="#dc2626" /> <span style={{ flex: 1 }}>My Profile</span>
                  </button>
                  <button onClick={() => { onProfileClick(); setMobileMenu(false); }} style={mobileNavStyle}>
                    <Package size={18} color="#dc2626" /> <span style={{ flex: 1 }}>My Orders</span>
                  </button>
                  <button onClick={() => { logout(); setMobileMenu(false); }} style={{ ...mobileNavStyle, color: '#dc2626' }}>
                    <LogOut size={18} /> <span style={{ flex: 1 }}>Logout</span>
                  </button>
                </>
              ) : (
                <button onClick={() => { navigate('/login'); setMobileMenu(false); }} style={mobileNavStyle}>
                  <User size={18} color="#dc2626" /> <span style={{ flex: 1 }}>Login / Sign Up</span>
                </button>
              )}
              
              <div style={{ marginTop: '24px', padding: '20px', background: '#fff5f5', borderTop: '1px solid rgba(220,38,38,0.1)' }}>
                <div style={{ fontSize: '12px', color: '#dc2626', marginBottom: '12px', fontWeight: 600 }}>Need Help?</div>
                <div style={{ fontSize: '11px', color: '#1a0a0a', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Phone size={14} color="#dc2626" /> +91-9130720201
                </div>
                <div style={{ fontSize: '11px', color: '#1a0a0a', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Mail size={14} color="#dc2626" /> pratikshaflowershop@gmail.com
                </div>
                <div style={{ fontSize: '11px', color: '#1a0a0a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <MapPin size={14} color="#dc2626" /> Solapur, Maharashtra
                </div>
              </div>
            </nav>
          </div>
        </>
      )}

      {/* Mobile Search Modal - Full screen with metadata search */}
      <MobileSearchModal 
        isOpen={showMobileSearch} 
        onClose={() => setShowMobileSearch(false)} 
        onSearch={(query) => { if (onSearch) onSearch(query); }}
        allProducts={allProducts}
        recentSearches={recentSearches}
        onClearRecent={clearRecentSearches}
        onAddToRecent={addToRecentSearches}
      />

      <style>{`
        @keyframes slideInLeft {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default Header;